import type { SupWeb3Address } from "@/types";
import { useCallback, useMemo } from "react";

import dappConfig from "@/../config/dapp.config";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import useAdmin from "@/hooks/api/useAdmin";
import useApi from "@/hooks/api/useApi";

export interface AppAdminConfig {
  isWithdrawalDisabled: boolean;
  isDepositDisabled: boolean;
  depositCartLimit: number;
  withdrawalCartLimit: number;
  rawConfig: ResponseAdminConfig;
}

/**
 * Fetch configuration for the admin panel
 *
 * @api {{GET}} /admin/config
 */
export interface ResponseAdminConfig {
  disable_all_non_xchain_deposits: boolean;
  disable_all_non_xchain_withdrawals: boolean;
  disable_all_xchain_deposits: boolean;
  disable_all_xchain_withdrawals: boolean;
  disable_dst_swaps: boolean;
  disable_keepers: boolean;
  disable_multivault_deposits: boolean;
  disable_multivault_withdrawals: boolean;
  exclude_ambs: string[];
  exclude_liquidity_providers: string[];
  exclude_from_chains_deposits: number[];
  exclude_from_chains_withdrawals: number[];
  exclude_from_vaults_withdrawals: string[];
  exclude_protocols_deposits: string[];
  exclude_protocols_withdrawals: string[];
  exclude_superposition_chains_withdrawals: number[];
  exclude_to_chains_deposits: number[];
  exclude_to_vaults_deposits: string[];
  maintenance_period_text: string;
  maintenance_period_type: string;
  num_vaults_in_bag_same_chain_deposit: number;
  num_vaults_in_bag_same_chain_withdraw: number;
  num_vaults_in_bag_xchain_deposit: number;
  num_vaults_in_bag_xchain_withdraw: number;
  super_admins: SupWeb3Address[];
  toast_text: string;
}

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-09563167-cffe-4c75-9759-35b5c87152a8?action=share&source=copy-link&creator=26633220&ctx=documentation
 */
export default function useGetAdminConfig() {
  const userAddress = useWeb3UserAddress();
  const { data: adminData } = useAdmin();

  const url = useMemo(() => `/admin/config/?userAddress=${userAddress}&for_admin=${adminData?.isAdmin ?? "false"}`, [userAddress, adminData?.isAdmin]);

  const handleResponse = useCallback(
    (data: ResponseAdminConfig): AppAdminConfig => {
      const overrideLimitations = dappConfig.isStaging || (adminData?.isAdmin ?? false);

      return {
        isWithdrawalDisabled: overrideLimitations ? false : data.disable_all_xchain_withdrawals && data.disable_all_non_xchain_withdrawals,
        isDepositDisabled: overrideLimitations ? false : data.disable_all_xchain_deposits && data.disable_all_non_xchain_deposits,
        depositCartLimit: overrideLimitations
          ? 10
          : data.disable_multivault_deposits
          ? 1
          : Math.max(data.num_vaults_in_bag_same_chain_deposit, data.num_vaults_in_bag_xchain_deposit),
        withdrawalCartLimit: overrideLimitations
          ? 10
          : data.disable_multivault_withdrawals
          ? 1
          : Math.max(data.num_vaults_in_bag_same_chain_withdraw, data.num_vaults_in_bag_xchain_withdraw),
        rawConfig: data,
      };
    },
    [adminData]
  );

  const { data, isLoading, isError, error } = useApi<AppAdminConfig, ResponseAdminConfig>(
    {
      path: url,
      api: "v2",
    },
    {
      strategy: "long-interval-refresh",
      select: handleResponse,
    }
  );

  return {
    data,
    isLoading,
    isError,
    error,
  };
}
